import React, { useState } from "react"
import {
    Button,
    ButtonVariant,
    CardSelect,
    type CardSelectProps,
    componentStyles,
    LEADING_VISUAL_TYPES,
    Text,
    useColors,
    useTheme,
    VerticalStack,
} from "@mallardbay/lib-react-components"

import type {
    UserSelectionMode,
    UserSelectionOption,
} from "~components/outfitter-assistant/user-selection-tool.helpers"
import { TEST_IDS } from "~config/constants"
import COPY from "~config/copy-constants"

interface Props {
    readonly prompt: string
    readonly options: readonly UserSelectionOption[]
    readonly mode: UserSelectionMode
    readonly isDisabled: boolean
    readonly onSelect: (labels: readonly string[]) => void
}

export default function AssistantUserSelectionCard({
    prompt,
    options,
    mode,
    isDisabled,
    onSelect,
}: Props) {
    const [selectedOptionIds, setSelectedOptionIds] = useState<
        readonly string[]
    >([])
    const [hasSubmitted, setHasSubmitted] = useState(false)
    const styles = useStyles()
    const isMultiSelect = mode === "multi"
    const isInteractionDisabled = isDisabled || hasSubmitted
    const selectOptions = buildCardSelectOptions({
        options,
        selectedOptionIds,
        isInteractionDisabled,
    })

    return (
        <VerticalStack
            style={styles.root}
            testId={TEST_IDS.ASSISTANT_USER_SELECTION_CARD}
        >
            <Text style={styles.prompt}>{prompt}</Text>
            <CardSelect
                options={selectOptions}
                onSelect={(option) => {
                    const nextSelectedOptionIds = getNextSelectedOptionIds({
                        selectedOptionIds,
                        clickedOptionId: option.value.id,
                        isMultiSelect,
                    })

                    setSelectedOptionIds(nextSelectedOptionIds)

                    if (!isMultiSelect) {
                        setHasSubmitted(true)
                        onSelect([option.value.label])
                    }
                }}
                isMultiSelect={isMultiSelect}
                optionCardStyle={styles.optionCard}
                showSelectionIndicator
            />
            {isMultiSelect ? (
                <Button
                    variant={ButtonVariant.Solid}
                    isDisabled={
                        isInteractionDisabled || selectedOptionIds.length === 0
                    }
                    onClick={() => {
                        const selectedLabels = getSelectedLabels({
                            options,
                            selectedOptionIds,
                        })

                        setHasSubmitted(true)
                        onSelect(selectedLabels)
                    }}
                >
                    {COPY.AI_CHAT_USER_SELECTION_SUBMIT}
                </Button>
            ) : null}
        </VerticalStack>
    )
}

function buildCardSelectOptions({
    options,
    selectedOptionIds,
    isInteractionDisabled,
}: {
    readonly options: readonly UserSelectionOption[]
    readonly selectedOptionIds: readonly string[]
    readonly isInteractionDisabled: boolean
}): CardSelectProps<UserSelectionOption>["options"] {
    return options.map((option) => ({
        value: option,
        title: option.label,
        subtitle: option.subtitle,
        isSelected: selectedOptionIds.includes(option.id),
        isDisabled: isInteractionDisabled,
        leadingVisual: option.imageUrl
            ? {
                  type: LEADING_VISUAL_TYPES.Image,
                  src: option.imageUrl,
                  alt: option.label,
              }
            : undefined,
    }))
}

function getNextSelectedOptionIds({
    selectedOptionIds,
    clickedOptionId,
    isMultiSelect,
}: {
    readonly selectedOptionIds: readonly string[]
    readonly clickedOptionId: string
    readonly isMultiSelect: boolean
}) {
    if (!isMultiSelect) return [clickedOptionId]

    if (selectedOptionIds.includes(clickedOptionId)) {
        return selectedOptionIds.filter(
            (optionId) => optionId !== clickedOptionId
        )
    }

    return [...selectedOptionIds, clickedOptionId]
}

function getSelectedLabels({
    options,
    selectedOptionIds,
}: {
    readonly options: readonly UserSelectionOption[]
    readonly selectedOptionIds: readonly string[]
}) {
    return options
        .filter((option) => selectedOptionIds.includes(option.id))
        .map((option) => option.label)
}

function useStyles() {
    const colors = useColors()
    const theme = useTheme()

    return componentStyles({
        root: {
            alignItems: "stretch",
            gap: 3,
            width: "100%",
        },
        prompt: {
            fontWeight: theme.fontWeights.semibold,
            color: colors.text,
        },
        optionCard: {
            minHeight: "56px",
            backgroundColor: colors.bgSecondary,
        },
    })
}
